import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

/**
 * Print-friendly analytics summary report. Pulls the same data as the
 * Analytics tab but renders it on a clean white page with a written
 * summary paragraph at the top and the 30-day trend strip front and
 * centre. Linked from Admin → Analytics → "Generate report" button.
 *
 * Tip: hit Cmd/Ctrl+P from this page to save as PDF or print.
 */
export default function AnalyticsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/admin/analytics/traffic");
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pad 30-day trend so the strip never looks gappy.
  const dailyPadded = (() => {
    const map = new Map((data?.daily || []).map((d) => [d.date, d]));
    const out = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push(map.get(key) || { date: key, views: 0, visitors: 0 });
    }
    return out;
  })();
  const maxViews = Math.max(1, ...dailyPadded.map((d) => d.views));
  const generatedAt = new Date().toLocaleString(undefined, {
    weekday: "short", year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });

  const totals = data?.totals || { views: {}, visitors: {} };
  const monthViews = totals.views?.month || 0;
  const monthVisitors = totals.visitors?.month || 0;
  const weekViews = totals.views?.week || 0;
  const weekVisitors = totals.visitors?.week || 0;
  const dayViews = totals.views?.day || 0;

  // Compare last 15 days vs the previous 15 to estimate momentum.
  const half = Math.floor(dailyPadded.length / 2);
  const recent = dailyPadded.slice(half).reduce((s, d) => s + d.views, 0);
  const prior = dailyPadded.slice(0, half).reduce((s, d) => s + d.views, 0);
  const trendPct = prior > 0 ? Math.round(((recent - prior) / prior) * 100) : null;
  const trendWord = trendPct === null
    ? "No comparison yet — we'll have one once 2 weeks of data accumulates"
    : trendPct > 0
      ? `Up ${trendPct}% vs the previous 15 days`
      : trendPct < 0
        ? `Down ${Math.abs(trendPct)}% vs the previous 15 days`
        : "Flat vs the previous 15 days";

  const topPage = (data?.top_pages || [])[0];
  const topReferrer = (data?.top_referrers || [])[0];
  const mobilePct = (() => {
    const devs = data?.devices || [];
    const tot = devs.reduce((s, d) => s + d.views, 0);
    if (!tot) return null;
    const m = devs.find((d) => d.device === "Mobile");
    return m ? Math.round((m.views / tot) * 100) : 0;
  })();

  const fmtPath = (p) => (p && p.length > 50 ? p.slice(0, 50) + "…" : p || "/");
  const fmtRef = (r) => {
    if (!r || r === "Direct") return "Direct";
    try { return new URL(r).hostname || r; } catch { return r.slice(0, 60); }
  };

  return (
    <div className="min-h-screen bg-white text-[#1C1C1C] print:bg-white">
      {/* Top toolbar — hidden on print */}
      <div className="print:hidden border-b border-black/[0.06] py-3 px-6 sticky top-0 bg-white z-10">
        <div className="max-w-[900px] mx-auto flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => window.close()}
            data-testid="report-close-btn"
            className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#1C1C1C]"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Close
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="report-print-btn"
            className="inline-flex items-center gap-1.5 text-sm bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-md px-3 py-2"
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-6 py-10 print:py-6" data-testid="analytics-report-page">
        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-2 pb-6 border-b border-black/[0.08]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">MyPlanCost · Analytics summary</div>
            <h1 className="fc-heading text-3xl font-extrabold mt-1">Last 30 days</h1>
          </div>
          <div className="text-[11px] text-[#A8A29E] fc-mono">Generated {generatedAt}</div>
        </div>

        {/* Narrative summary */}
        <div className="mt-6 text-[14px] leading-relaxed text-[#1C1C1C] space-y-2.5" data-testid="report-narrative">
          {loading ? (
            <p className="text-[#A8A29E]">Loading the latest numbers…</p>
          ) : (
            <>
              <p>
                <strong>{monthVisitors.toLocaleString()}</strong> unique visitors generated{" "}
                <strong>{monthViews.toLocaleString()}</strong> page views over the last 30 days
                {monthVisitors > 0 && (
                  <> — an average of <strong>{(monthViews / monthVisitors).toFixed(1)} views per visitor</strong></>
                )}.
                {" "}
                {trendWord}.
              </p>
              <p>
                In the last 7 days you had <strong>{weekVisitors.toLocaleString()} visitors</strong> and{" "}
                <strong>{weekViews.toLocaleString()} views</strong>; in the last 24 hours, <strong>{dayViews.toLocaleString()}</strong> views.
                {topPage && (
                  <>
                    {" "}Your most-visited page was{" "}
                    <code className="text-[13px] bg-sage-50 px-1.5 py-0.5 rounded">{fmtPath(topPage.path)}</code>{" "}
                    with {topPage.views.toLocaleString()} views from {topPage.visitors} visitors.
                  </>
                )}
                {topReferrer && (
                  <>
                    {" "}The largest traffic source was <strong>{fmtRef(topReferrer.referrer)}</strong>{" "}
                    ({topReferrer.views.toLocaleString()} views).
                  </>
                )}
              </p>
              {mobilePct !== null && (
                <p>
                  Mobile devices accounted for <strong>{mobilePct}%</strong> of views.
                  {mobilePct >= 40
                    ? " A meaningful share — worth keeping the mobile experience tight."
                    : mobilePct >= 20
                      ? " A growing share, but desktop still dominates."
                      : " Desktop-heavy audience, but it's worth checking your mobile screens regularly."}
                </p>
              )}
            </>
          )}
        </div>

        {/* 30-day trend strip */}
        <section className="mt-8" data-testid="report-trend-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="fc-heading text-lg font-medium">30-day trend · views per day</h2>
            <div className="text-[11px] text-[#A8A29E] fc-mono">Peak: {maxViews.toLocaleString()}</div>
          </div>
          <div className="flex items-end gap-1 h-32 border border-black/[0.06] rounded-md p-3 bg-[#FCFBF8]" data-testid="report-trend-strip">
            {dailyPadded.map((d) => {
              const pct = (d.views / maxViews) * 100;
              return (
                <div
                  key={d.date}
                  className="flex-1 bg-sage-300 rounded-sm min-h-[2px] hover:bg-sage-500 transition-colors print:bg-sage-500"
                  style={{ height: `${Math.max(2, pct)}%` }}
                  title={`${d.date} · ${d.views} views · ${d.visitors} visitors`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-[#A8A29E] mt-2 fc-mono">
            <span>{dailyPadded[0]?.date}</span>
            <span>{dailyPadded[dailyPadded.length - 1]?.date}</span>
          </div>
        </section>

        {/* KPI grid */}
        <section className="mt-8 grid grid-cols-3 gap-3">
          {[
            { label: "Last 24 hours", views: totals.views?.day, visitors: totals.visitors?.day },
            { label: "Last 7 days", views: weekViews, visitors: weekVisitors },
            { label: "Last 30 days", views: monthViews, visitors: monthVisitors },
          ].map((tile) => (
            <div key={tile.label} className="border border-black/[0.08] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">{tile.label}</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-[#A8A29E]">Visitors</div>
                  <div className="fc-mono text-lg">{(tile.visitors || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#A8A29E]">Views</div>
                  <div className="fc-mono text-lg">{(tile.views || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Top pages + referrers */}
        <section className="mt-8 grid md:grid-cols-2 gap-5 print:grid-cols-2">
          <div>
            <h2 className="fc-heading text-lg font-medium mb-2">Top pages</h2>
            <table className="w-full text-sm border border-black/[0.06] rounded-md overflow-hidden">
              <tbody>
                {(data?.top_pages || []).slice(0, 10).map((p) => (
                  <tr key={p.path} className="border-b border-black/[0.04] last:border-0">
                    <td className="py-2 px-3 truncate max-w-[180px]" title={p.path}>{fmtPath(p.path)}</td>
                    <td className="py-2 px-3 text-right fc-mono text-[12px]">{p.visitors} v</td>
                    <td className="py-2 px-3 text-right fc-mono">{p.views.toLocaleString()}</td>
                  </tr>
                ))}
                {(!loading && (data?.top_pages || []).length === 0) && (
                  <tr><td colSpan={3} className="py-6 text-center text-[#A8A29E] text-[12px]">No page views yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            <h2 className="fc-heading text-lg font-medium mb-2">Top referrers</h2>
            <table className="w-full text-sm border border-black/[0.06] rounded-md overflow-hidden">
              <tbody>
                {(data?.top_referrers || []).slice(0, 10).map((r) => (
                  <tr key={r.referrer} className="border-b border-black/[0.04] last:border-0">
                    <td className="py-2 px-3 truncate max-w-[220px]" title={r.referrer}>{fmtRef(r.referrer)}</td>
                    <td className="py-2 px-3 text-right fc-mono">{r.views.toLocaleString()}</td>
                  </tr>
                ))}
                {(!loading && (data?.top_referrers || []).length === 0) && (
                  <tr><td colSpan={2} className="py-6 text-center text-[#A8A29E] text-[12px]">No referrers tracked yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-black/[0.06] text-[10px] uppercase tracking-[0.2em] text-[#A8A29E] flex justify-between">
          <span>MyPlanCost · FlipCost &amp; SpaceCost</span>
          <span>Cookie-free analytics · 90-day retention</span>
        </div>
      </div>
    </div>
  );
}
